/** Getrennte Backup-Ordner via File System Access API (Chrome/Edge).
 *  Slot `master` = 24h Master-Mandanten-Backup (Admin)
 *  Slot `global` = 72h Global-Settings-Backup (Admin)
 *  Slot `client` = PC-Safe des Mandanten (fester Ort + 24h / Logout)
 */

export type BackupFolderSlot = 'master' | 'global' | 'client';

const DB_NAME = 'pe_backup_folder_v1';
const STORE = 'handles';

/** Legacy (ein gemeinsamer Ordner) — wird beim ersten Lesen auf `master` migriert. */
const LEGACY_HANDLE_KEY = 'dir';
const LEGACY_NAME_KEY = 'PE_BACKUP_FOLDER_NAME';

const SLOT_META: Record<
    BackupFolderSlot,
    { handleKey: string; nameKey: string; pickerId: string; label: string }
> = {
    master: {
        handleKey: 'dir_master',
        nameKey: 'PE_BACKUP_FOLDER_NAME_MASTER',
        pickerId: 'preisexpert-backups-master',
        label: 'Master-Mandanten-Backup',
    },
    global: {
        handleKey: 'dir_global',
        nameKey: 'PE_BACKUP_FOLDER_NAME_GLOBAL',
        pickerId: 'preisexpert-backups-global',
        label: 'Global-Backup',
    },
    client: {
        handleKey: 'dir_client',
        nameKey: 'PE_BACKUP_FOLDER_NAME_CLIENT',
        pickerId: 'preisexpert-backups-client',
        label: 'PC-Safe Mandant',
    },
};

/** Timestamp des letzten erfolgreichen Mandanten-PC-Safe (localStorage). */
export const CLIENT_PC_SAFE_TIMESTAMP_KEY = 'LAST_CLIENT_PC_SAFE_TIMESTAMP';
export const CLIENT_PC_SAFE_INTERVAL_MS = 24 * 60 * 60 * 1000;

export const getLastClientPcSafeAt = (): number | null => {
    try {
        const raw = localStorage.getItem(CLIENT_PC_SAFE_TIMESTAMP_KEY);
        if (!raw) return null;
        const n = parseInt(raw, 10);
        return Number.isFinite(n) ? n : null;
    } catch {
        return null;
    }
};

export const markClientPcSafeDone = (): void => {
    try {
        localStorage.setItem(CLIENT_PC_SAFE_TIMESTAMP_KEY, Date.now().toString());
    } catch {
        /* ignore */
    }
};

export const isClientPcSafeDue = (): boolean => {
    const last = getLastClientPcSafeAt();
    if (last == null) return true;
    return Date.now() - last > CLIENT_PC_SAFE_INTERVAL_MS;
};

const openDb = (): Promise<IDBDatabase> =>
    new Promise((resolve, reject) => {
        const req = indexedDB.open(DB_NAME, 1);
        req.onupgradeneeded = () => {
            const db = req.result;
            if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error || new Error('IndexedDB open failed'));
    });

export const supportsBackupFolderPicker = (): boolean =>
    typeof window !== 'undefined' && typeof (window as any).showDirectoryPicker === 'function';

const idbGet = async (key: string): Promise<FileSystemDirectoryHandle | null> => {
    try {
        const db = await openDb();
        const handle = await new Promise<FileSystemDirectoryHandle | null>((resolve, reject) => {
            const tx = db.transaction(STORE, 'readonly');
            const req = tx.objectStore(STORE).get(key);
            req.onsuccess = () => resolve((req.result as FileSystemDirectoryHandle) || null);
            req.onerror = () => reject(req.error);
        });
        db.close();
        return handle;
    } catch {
        return null;
    }
};

const idbPut = async (key: string, handle: FileSystemDirectoryHandle): Promise<void> => {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
        const tx = db.transaction(STORE, 'readwrite');
        tx.objectStore(STORE).put(handle, key);
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
    });
    db.close();
};

const idbDelete = async (key: string): Promise<void> => {
    try {
        const db = await openDb();
        await new Promise<void>((resolve, reject) => {
            const tx = db.transaction(STORE, 'readwrite');
            tx.objectStore(STORE).delete(key);
            tx.oncomplete = () => resolve();
            tx.onerror = () => reject(tx.error);
        });
        db.close();
    } catch {
        /* ignore */
    }
};

/** Einmalig: alter Ein-Ordner-Stand → Master-Slot (Global bleibt leer). */
const migrateLegacyToMasterIfNeeded = async (): Promise<void> => {
    const meta = SLOT_META.master;
    try {
        const already = localStorage.getItem(meta.nameKey);
        if (already) return;
        const legacyName = localStorage.getItem(LEGACY_NAME_KEY);
        if (!legacyName) return;
        localStorage.setItem(meta.nameKey, legacyName);
        localStorage.removeItem(LEGACY_NAME_KEY);
    } catch {
        /* ignore */
    }
    try {
        const existingMaster = await idbGet(meta.handleKey);
        if (existingMaster) return;
        const legacyHandle = await idbGet(LEGACY_HANDLE_KEY);
        if (!legacyHandle) return;
        await idbPut(meta.handleKey, legacyHandle);
        await idbDelete(LEGACY_HANDLE_KEY);
    } catch {
        /* ignore */
    }
};

export const getSavedBackupFolderName = (slot: BackupFolderSlot = 'master'): string => {
    try {
        if (slot === 'master') {
            // Sync-Pfad: Legacy-Name sofort anzeigen, IDB-Migration läuft async beim Load
            const legacy = localStorage.getItem(LEGACY_NAME_KEY);
            const current = localStorage.getItem(SLOT_META.master.nameKey);
            if (!current && legacy) {
                try {
                    localStorage.setItem(SLOT_META.master.nameKey, legacy);
                    localStorage.removeItem(LEGACY_NAME_KEY);
                } catch {
                    return legacy;
                }
            }
        }
        return localStorage.getItem(SLOT_META[slot].nameKey) || '';
    } catch {
        return '';
    }
};

export const clearSavedBackupFolder = async (slot: BackupFolderSlot = 'master'): Promise<void> => {
    const meta = SLOT_META[slot];
    try {
        localStorage.removeItem(meta.nameKey);
        if (slot === 'master') localStorage.removeItem(LEGACY_NAME_KEY);
    } catch {
        /* ignore */
    }
    await idbDelete(meta.handleKey);
    if (slot === 'master') await idbDelete(LEGACY_HANDLE_KEY);
};

export const saveBackupDirectoryHandle = async (
    handle: FileSystemDirectoryHandle,
    slot: BackupFolderSlot = 'master'
): Promise<void> => {
    const meta = SLOT_META[slot];
    await idbPut(meta.handleKey, handle);
    try {
        localStorage.setItem(meta.nameKey, handle.name || meta.label);
        if (slot === 'master') localStorage.removeItem(LEGACY_NAME_KEY);
    } catch {
        /* ignore */
    }
};

export const loadBackupDirectoryHandle = async (
    slot: BackupFolderSlot = 'master'
): Promise<FileSystemDirectoryHandle | null> => {
    if (slot === 'master') await migrateLegacyToMasterIfNeeded();
    const meta = SLOT_META[slot];
    let handle = await idbGet(meta.handleKey);
    if (!handle && slot === 'master') {
        handle = await idbGet(LEGACY_HANDLE_KEY);
        if (handle) {
            await idbPut(meta.handleKey, handle);
            await idbDelete(LEGACY_HANDLE_KEY);
        }
    }
    return handle;
};

export const ensureBackupFolderPermission = async (
    handle: FileSystemDirectoryHandle
): Promise<boolean> => {
    const opts = { mode: 'readwrite' as const };
    try {
        if ((await (handle as any).queryPermission(opts)) === 'granted') return true;
        if ((await (handle as any).requestPermission(opts)) === 'granted') return true;
    } catch {
        return false;
    }
    return false;
};

export const pickBackupDirectory = async (
    slot: BackupFolderSlot = 'master'
): Promise<FileSystemDirectoryHandle | null> => {
    if (!supportsBackupFolderPicker()) return null;
    const meta = SLOT_META[slot];
    try {
        const handle = await (window as any).showDirectoryPicker({
            id: meta.pickerId,
            mode: 'readwrite',
            startIn: 'documents',
        });
        await saveBackupDirectoryHandle(handle, slot);
        return handle;
    } catch (err: any) {
        if (err?.name === 'AbortError') return null;
        throw err;
    }
};

export const writeJsonToBackupFolder = async (
    handle: FileSystemDirectoryHandle,
    fileName: string,
    jsonText: string
): Promise<void> => {
    const fileHandle = await handle.getFileHandle(fileName, { create: true });
    const writable = await (fileHandle as any).createWritable();
    await writable.write(jsonText);
    await writable.close();
};

/** Schreibt in den Slot-Ordner wenn möglich; sonst klassischer Browser-Download. */
export const saveBackupJson = async (
    fileName: string,
    data: unknown,
    slot: BackupFolderSlot = 'master'
): Promise<'folder' | 'download'> => {
    const jsonText = JSON.stringify(data, null, 2);
    const handle = await loadBackupDirectoryHandle(slot);
    if (handle && (await ensureBackupFolderPermission(handle))) {
        await writeJsonToBackupFolder(handle, fileName, jsonText);
        return 'folder';
    }

    const blob = new Blob([jsonText], { type: 'application/json;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    return 'download';
};

export const getBackupFolderSlotLabel = (slot: BackupFolderSlot): string => SLOT_META[slot].label;
